import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Copy, Package } from "lucide-react";

const EMPTY = {
  nome: "", descrizione_breve: "", descrizione_estesa: "", categoria: "generale",
  prezzo_unitario: 0, unita: "anno", iva: 22, attivo: true, modificabile_offerta: true,
  ordine: 0, colore: "blue", note_interne: "",
};

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/catalog");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.nome) return toast.error("Nome obbligatorio");
    try {
      if (editing.id) {
        await api.patch(`/catalog/${editing.id}`, editing);
        toast.success("Voce aggiornata");
      } else {
        await api.post("/catalog", editing);
        toast.success("Voce creata");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare la voce?")) return;
    try { await api.delete(`/catalog/${id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error("Errore"); }
  };
  const duplicate = async (id) => {
    try { await api.post(`/catalog/${id}/duplicate`); toast.success("Duplicata"); load(); } catch (e) { toast.error("Errore"); }
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.categoria] = acc[it.categoria] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Amministrazione</div>
          <h1 className="font-display text-3xl font-bold text-[#0F2C59]">Listino servizi</h1>
          <p className="text-slate-500">Gestisci prezzi, descrizioni e categorie del listino.</p>
        </div>
        <Button onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="new-catalog-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova voce
        </Button>
      </div>

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{cat}</div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600 border-b border-slate-300">
                <tr>
                  <th className="text-left px-4 py-2.5">Nome</th>
                  <th className="text-left px-4 py-2.5">Unità</th>
                  <th className="text-right px-4 py-2.5">Prezzo</th>
                  <th className="text-center px-4 py-2.5">Attivo</th>
                  <th className="text-right px-4 py-2.5 w-32">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {list.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{it.nome}</div>
                      {it.descrizione_breve && <div className="text-xs text-slate-500">{it.descrizione_breve}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{it.unita}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtEur(it.prezzo_unitario)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${it.attivo ? "bg-green-50 text-green-700 border-green-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                        {it.attivo ? "Attivo" : "Off"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => { setEditing(it); setOpen(true); }}
                          className="p-1.5 rounded border border-slate-200 bg-white hover:bg-blue-50 hover:border-[#0F2C59] text-[#0F2C59] transition-colors"
                          title="Modifica voce"
                          data-testid={`edit-cat-${it.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => duplicate(it.id)}
                          className="p-1.5 rounded border border-slate-200 bg-white hover:bg-yellow-50 hover:border-[#FACC15] text-slate-600 transition-colors"
                          title="Duplica"
                          data-testid={`dup-cat-${it.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => del(it.id)}
                          className="p-1.5 rounded border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 text-red-500 transition-colors"
                          title="Elimina"
                          data-testid={`del-cat-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica voce" : "Nuova voce di listino"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome</Label><Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} className="mt-1.5" data-testid="cat-nome" /></div>
              <div><Label>Descrizione breve</Label><Input value={editing.descrizione_breve} onChange={(e) => setEditing({ ...editing, descrizione_breve: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Descrizione estesa (PDF)</Label><Textarea rows={3} value={editing.descrizione_estesa} onChange={(e) => setEditing({ ...editing, descrizione_estesa: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Categoria</Label><Input value={editing.categoria} onChange={(e) => setEditing({ ...editing, categoria: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Prezzo (€)</Label><Input type="number" step="0.01" value={editing.prezzo_unitario} onChange={(e) => setEditing({ ...editing, prezzo_unitario: Number(e.target.value) })} className="mt-1.5" data-testid="cat-prezzo" /></div>
                <div>
                  <Label>Unità</Label>
                  <Select value={editing.unita} onValueChange={(v) => setEditing({ ...editing, unita: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["anno", "mese", "ora", "km", "pasto", "accesso", "forfait", "dipendente"].map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>IVA %</Label><Input type="number" value={editing.iva} onChange={(e) => setEditing({ ...editing, iva: Number(e.target.value) })} className="mt-1.5" /></div>
                <div><Label>Ordine</Label><Input type="number" value={editing.ordine} onChange={(e) => setEditing({ ...editing, ordine: Number(e.target.value) })} className="mt-1.5" /></div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2"><Checkbox checked={editing.attivo} onCheckedChange={(v) => setEditing({ ...editing, attivo: !!v })} /> <span className="text-sm">Attivo</span></label>
                <label className="flex items-center gap-2"><Checkbox checked={editing.modificabile_offerta} onCheckedChange={(v) => setEditing({ ...editing, modificabile_offerta: !!v })} /> <span className="text-sm">Modificabile in offerta</span></label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2C59]" data-testid="save-cat-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
